/**
 * Spike metrics — how far a posed one-ring has poked out of the surface it
 * sat in at rest, and how sharply.
 *
 * PROVENANCE. This is Astra's `slender-spike-metrics` from the
 * `investigate/slender-elbows` study, ported VERBATIM apart from this header
 * and the `selected`-restricted scale pass below. It is ported rather than
 * re-derived on purpose: Allen approved the combined repair BY EYE across
 * every case he tested, and a rewrite — however tidy — would silently change
 * the thing he approved. The one behavioural liberty taken is documented at
 * its site.
 *
 * The measurements are all COMPARATIVE — posed against rest, never absolute —
 * so they are scale-free and survive a character of any size:
 *
 *   excessInEdgeLengths  how much further the vertex sits from its ring's
 *                        centroid than it did at rest, in local edge lengths
 *   neighborContrast     that excess against its neighbours' median — a spike
 *                        is a LOCAL outlier, not a region that moved together
 *   posed/restFaceAngle  median dihedral across incident edges: new sharpness
 *   closed               every incident edge has exactly two faces; an open or
 *                        non-manifold vertex is reported and never repaired
 *
 * Residual is UNSIGNED, so a sharp inward pinch satisfies the same tests as an
 * outward spike. The repair pass is what decides direction; this module only
 * measures.
 *
 * Depends on: three. Depended on by: soften-correction.
 */
import { Vector3 } from 'three';

export function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length === 0
    ? 0
    : sorted.length % 2
      ? sorted[mid]!
      : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

export function spikeTopology(rest: Vector3[], triangles: ArrayLike<number>, selected: Set<number>) {
  const neighbors = rest.map(() => new Set<number>());
  const faces = rest.map(() => [] as number[]);
  const edges = new Map<string, { a: number; b: number; faces: number[] }>();
  for (let t = 0; t < triangles.length; t += 3) {
    const tri = [triangles[t]!, triangles[t + 1]!, triangles[t + 2]!];
    for (const v of tri) faces[v]!.push(t);
    for (let k = 0; k < 3; k++) {
      const a = tri[k]!,
        b = tri[(k + 1) % 3]!;
      if (a === b) continue;
      neighbors[a]!.add(b);
      neighbors[b]!.add(a);
      const key = `${Math.min(a, b)}:${Math.max(a, b)}`;
      const edge = edges.get(key) ?? { a, b, faces: [] };
      edge.faces.push(t);
      edges.set(key, edge);
    }
  }
  const incident = rest.map(() => [] as Array<{ a: number; b: number; faces: number[] }>);
  for (const edge of edges.values()) {
    incident[edge.a]!.push(edge);
    incident[edge.b]!.push(edge);
  }
  return { rest, triangles, selected, neighbors, faces, incident };
}

export type SpikeTopology = ReturnType<typeof spikeTopology>;

export function measureSpikes(topology: SpikeTopology, posed: Vector3[]) {
  const { rest, triangles, selected, neighbors, incident } = topology;
  const residual = (positions: Vector3[], v: number) => {
    const ns = [...neighbors[v]!];
    if (!ns.length) return 0;
    const center = ns
      .reduce((sum, n) => sum.add(positions[n]!), new Vector3())
      .multiplyScalar(1 / ns.length);
    return positions[v]!.distanceTo(center);
  };
  const normal = (positions: Vector3[], face: number) => {
    const a = positions[triangles[face]!]!,
      b = positions[triangles[face + 1]!]!,
      c = positions[triangles[face + 2]!]!;
    const n = b.clone().sub(a).cross(c.clone().sub(a));
    return n.lengthSq() < 1e-24 ? null : n.normalize();
  };
  const corner = (positions: Vector3[], v: number) => {
    const angles: number[] = [];
    for (const edge of incident[v]!) {
      if (edge.faces.length !== 2) continue;
      const a = normal(positions, edge.faces[0]!),
        b = normal(positions, edge.faces[1]!);
      if (a && b) angles.push((Math.acos(Math.min(1, Math.max(-1, a.dot(b)))) * 180) / Math.PI);
    }
    return median(angles);
  };
  // THE ONE LIBERTY. The study computed `scale` and `excess` for EVERY vertex
  // of the mesh. Here the same measurement runs at bake time inside the crease
  // deriver, once per hinge per sample angle, on meshes of 100k+ vertices — and
  // only `selected` and its immediate ring are ever read (contrast reads a
  // neighbour's excess). So both are computed lazily and memoised. Same
  // numbers, asked for fewer times; a vertex never asked about is never a row.
  const scaleCache = new Map<number, number>();
  const scaleOf = (v: number): number => {
    let s = scaleCache.get(v);
    if (s === undefined) {
      s = median([...neighbors[v]!].map((n) => rest[v]!.distanceTo(rest[n]!)));
      scaleCache.set(v, s);
    }
    return s;
  };
  const excessCache = new Map<number, number>();
  const excessOf = (v: number): number => {
    let e = excessCache.get(v);
    if (e === undefined) {
      const s = scaleOf(v);
      e = s > 1e-12 ? Math.max(0, (residual(posed, v) - residual(rest, v)) / s) : 0;
      excessCache.set(v, e);
    }
    return e;
  };
  const rows = [...selected]
    .filter((v) => neighbors[v]!.size >= 3 && scaleOf(v) > 1e-12)
    .map((v) => {
      const localMedian = median([...neighbors[v]!].map((n) => excessOf(n)));
      const contrast = excessOf(v) / Math.max(0.05, localMedian);
      const restAngle = corner(rest, v),
        posedAngle = corner(posed, v);
      const stretch = median(
        [...neighbors[v]!].map(
          (n) => posed[v]!.distanceTo(posed[n]!) / Math.max(1e-12, rest[v]!.distanceTo(rest[n]!)),
        ),
      );
      const closed = incident[v]!.every((edge) => edge.faces.length === 2);
      // Exploratory thresholds only: require a newly protruding local outlier AND new face sharpness.
      // Residual is unsigned; a sharp inward pinch can also satisfy these tests.
      const candidate = closed && excessOf(v) > 0.35 && contrast > 2 && posedAngle - restAngle > 20;
      return {
        vertex: v,
        neighbors: neighbors[v]!.size,
        closed,
        excessInEdgeLengths: excessOf(v),
        neighborContrast: contrast,
        restFaceAngle: restAngle,
        posedFaceAngle: posedAngle,
        medianEdgeStretch: stretch,
        candidate,
        score: (excessOf(v) * Math.min(contrast, 10) * Math.max(0, posedAngle - restAngle)) / 90,
      };
    });
  rows.sort((a, b) => b.score - a.score);
  return {
    candidateCount: rows.filter((r) => r.candidate).length,
    eligibleVertices: rows.filter((r) => r.closed).length,
    top: rows.slice(0, 10),
    rows,
  };
}
